/**
 * 日曆引擎的純整數日期／時間工具。
 *
 * 設計約束：
 * - core 內部不建立、不儲存 `Date` 物件，也不引用任何第三方日期套件。
 * - 日期一律是 local wall-clock 的 branded 字串，時間單位一律為分鐘。
 * - 這裡的換算全部是整數運算（Howard Hinnant 的 days-from-civil 演算法），
 *   因此不受時區、DST、locale 影響，結果完全可重現。
 */

import { isoDate, isoDateTime, type ISODate, type ISODateTime } from '../domain/branded.js';
import { InvalidArgumentError } from '../domain/errors.js';

/** 一天的分鐘數。 */
export const MINUTES_PER_DAY = 1440;

/** 民用日期（year / month 1-12 / day 1-31）。 */
export interface CivilDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

/** 拆解後的日期時間：距離 1970-01-01 的天數 + 當日分鐘數。 */
export interface DayMinute {
  /** 距離 1970-01-01 的天數（可為負）。 */
  readonly day: number;
  /** 當日分鐘數，0 <= minute < 1440。 */
  readonly minute: number;
}

/** 民用日期 → 距離 1970-01-01 的天數。 */
export function daysFromCivil(year: number, month: number, day: number): number {
  const y = year - (month <= 2 ? 1 : 0);
  const era = Math.floor((y >= 0 ? y : y - 399) / 400);
  const yoe = y - era * 400; // [0, 399]
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1; // [0, 365]
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
  return era * 146097 + doe - 719468;
}

/** 距離 1970-01-01 的天數 → 民用日期。 */
export function civilFromDays(days: number): CivilDate {
  const z = days + 719468;
  const era = Math.floor((z >= 0 ? z : z - 146096) / 146097);
  const doe = z - era * 146097; // [0, 146096]
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153); // [0, 11]
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1; // [1, 31]
  const m = mp + (mp < 10 ? 3 : -9); // [1, 12]
  return { year: y + (m <= 2 ? 1 : 0), month: m, day: d };
}

/** 星期索引，0 = 週日 … 6 = 週六（與 `Date#getDay()` 一致）。 */
export function weekDayOf(days: number): number {
  return ((days + 4) % 7 + 7) % 7;
}

const pad2 = (value: number): string => (value < 10 ? `0${value}` : String(value));

const pad4 = (value: number): string => {
  if (value >= 1000) return String(value);
  if (value < 0) return `-${pad4(-value)}`;
  return String(value).padStart(4, '0');
};

/**
 * 支援的年份範圍。
 * 下限刻意設在 1900：專案管理不需要更早的年份，而排除負紀元也避免了
 * proleptic Gregorian 在西元 1 年以前的換算歧義。
 */
export const MIN_YEAR = 1900;
export const MAX_YEAR = 9999;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** 是否為閏年（proleptic Gregorian）。 */
export function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

/** 該年該月的天數；month 超出 1–12 時回傳 0。 */
export function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) return 0;
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return DAYS_IN_MONTH[month - 1] ?? 0;
}

/** 該民用日期是否真實存在（會擋掉 2026-02-31、2027-02-29 之類）。 */
export function isValidCivilDate(year: number, month: number, day: number): boolean {
  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  if (month < 1 || month > 12) return false;
  return day >= 1 && day <= daysInMonth(year, month);
}

const digitsAt = (text: string, offset: number, length: number): number => {
  let value = 0;
  for (let i = offset; i < offset + length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 48 || code > 57) return Number.NaN;
    value = value * 10 + (code - 48);
  }
  return value;
};

const fail = (kind: string, text: string, detail: string): never => {
  throw new InvalidArgumentError(`Invalid ${kind} "${text}": ${detail}`);
};

/**
 * 解析並嚴格驗證 `YYYY-MM-DD` 前綴，回傳民用日期。
 * 不做任何正規化：`2026-02-31` 會拋錯，而不是被捲成 2026-03-03。
 */
function parseCivilPrefix(text: string, kind: string): CivilDate {
  if (text.charCodeAt(4) !== 45 /* - */ || text.charCodeAt(7) !== 45) {
    fail(kind, text, 'expected YYYY-MM-DD with "-" separators');
  }
  const year = digitsAt(text, 0, 4);
  const month = digitsAt(text, 5, 2);
  const day = digitsAt(text, 8, 2);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    fail(kind, text, 'expected digits in YYYY-MM-DD');
  }
  if (year < MIN_YEAR || year > MAX_YEAR) {
    fail(kind, text, `year must be between ${MIN_YEAR} and ${MAX_YEAR}`);
  }
  if (month < 1 || month > 12) fail(kind, text, 'month must be between 01 and 12');
  const maxDay = daysInMonth(year, month);
  if (day < 1 || day > maxDay) {
    fail(kind, text, `day must be between 01 and ${String(maxDay).padStart(2, '0')}`);
  }
  return { year, month, day };
}

/** `YYYY-MM-DD` → 距離 1970-01-01 的天數。格式或日期不合法時拋 InvalidArgumentError。 */
export function parseDate(value: ISODate): number {
  const text = value as string;
  if (text.length !== 10) {
    fail('ISODate', text, 'expected exactly 10 characters (YYYY-MM-DD)');
  }
  const civil = parseCivilPrefix(text, 'ISODate');
  return daysFromCivil(civil.year, civil.month, civil.day);
}

/**
 * `YYYY-MM-DDTHH:mm` 或 `YYYY-MM-DDTHH:mm:ss` → { day, minute }。
 *
 * - 嚴格驗證：分隔符、月／日／時／分／秒範圍、閏日皆檢查，不合法即拋
 *   `InvalidArgumentError`，絕不靜默正規化（MSPDI 等外部輸入必經此處）。
 * - 不接受時區標記（`Z`、`+08:00`）；契約規定是 local wall-clock。
 * - 秒數以「不足一分鐘」處理：計算一律在分鐘粒度上進行，秒被無條件捨去。
 */
export function parseDateTime(value: ISODateTime): DayMinute {
  const text = value as string;
  if (text.length !== 16 && text.length !== 19) {
    fail('ISODateTime', text, 'expected YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss');
  }
  const civil = parseCivilPrefix(text, 'ISODateTime');
  if (text.charCodeAt(10) !== 84 /* T */ || text.charCodeAt(13) !== 58 /* : */) {
    fail('ISODateTime', text, 'expected "T" and ":" separators');
  }
  const hour = digitsAt(text, 11, 2);
  const minuteOfHour = digitsAt(text, 14, 2);
  if (Number.isNaN(hour) || Number.isNaN(minuteOfHour)) {
    fail('ISODateTime', text, 'expected digits in HH:mm');
  }
  if (hour > 23) fail('ISODateTime', text, 'hour must be between 00 and 23');
  if (minuteOfHour > 59) fail('ISODateTime', text, 'minute must be between 00 and 59');
  if (text.length === 19) {
    if (text.charCodeAt(16) !== 58) fail('ISODateTime', text, 'expected ":" before seconds');
    const second = digitsAt(text, 17, 2);
    if (Number.isNaN(second)) fail('ISODateTime', text, 'expected digits in ss');
    if (second > 59) fail('ISODateTime', text, 'second must be between 00 and 59');
  }
  return { day: daysFromCivil(civil.year, civil.month, civil.day), minute: hour * 60 + minuteOfHour };
}

/** 將 (day, minute) 正規化到 0 <= minute < 1440。 */
export function normalize(day: number, minute: number): DayMinute {
  const carry = Math.floor(minute / MINUTES_PER_DAY);
  return { day: day + carry, minute: minute - carry * MINUTES_PER_DAY };
}

/** 距離 1970-01-01 的天數 → `YYYY-MM-DD`。 */
export function formatDate(day: number): ISODate {
  const civil = civilFromDays(day);
  return isoDate(`${pad4(civil.year)}-${pad2(civil.month)}-${pad2(civil.day)}`);
}

/** (day, minute) → `YYYY-MM-DDTHH:mm:ss`（自動正規化跨日的分鐘數）。 */
export function formatDateTime(day: number, minute: number): ISODateTime {
  const at = normalize(day, minute);
  const civil = civilFromDays(at.day);
  const hh = Math.floor(at.minute / 60);
  const mm = at.minute - hh * 60;
  return isoDateTime(
    `${pad4(civil.year)}-${pad2(civil.month)}-${pad2(civil.day)}T${pad2(hh)}:${pad2(mm)}:00`,
  );
}

/** 兩個 (day, minute) 的先後比較：a < b 回傳負值。 */
export function compareDayMinute(a: DayMinute, b: DayMinute): number {
  return a.day !== b.day ? a.day - b.day : a.minute - b.minute;
}
